import {
    ADDITIONAL_LIMIT_ISSUE,
    BLOCK_THREE_MONTH_LIMIT_ISSUE,
    COMMON_AMOUNT_ISSUE,
    MAX_AMOUNT_BONUS_ISSUE,
    MAX_AMOUNT_BONUS_LIMIT_ISSUE,
    MAX_AMOUNT_ISSUE,
    MIN_AMOUNT_ISSUE,
    MULTIPLE_AMOUNT_ISSUE,
    PROFILE_LIMIT_ISSUE,
    USA_PROFILE_LIMIT_ISSUE,
} from 'apps/auth/constants/Constants';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { LimitsService, type AdditionalLimit, type CommissionInfo, type ProfileWithLimits } from 'apps/auth/services/LimitsService';

type ValidationMessages = Record<string, string | undefined>;

type ExtraData = {
    canMakeDraftTransaction?: boolean;
    commission?: CommissionInfo;
    additionalAmlLimit?: AdditionalLimit | null;
} & Record<string, unknown>;

type AuthData = {
    countryId?: number | string;
} & Record<string, unknown>;

type ValidationResult = {
    error: {
        code: string;
        body?: string;
    };
    errorMessage?: string;
    isError: boolean;
    isShowToViewDetails: boolean;
};

export class SendingAmountValidationService {
    errorMessages: ValidationMessages = {};
    isBonus = false;
    leftLimitAmount?: number | null;
    profile: ProfileWithLimits = {};
    extra: ExtraData = {};
    auth: AuthData = {};

    constructor(
        errorMessages: ValidationMessages,
        leftLimitAmount: number | null | undefined,
        isBonus: boolean,
        profile: ProfileWithLimits,
        extra: ExtraData,
        auth: AuthData,
    ) {
        this.errorMessages = errorMessages;
        this.leftLimitAmount = leftLimitAmount;
        this.isBonus = isBonus;
        this.profile = profile;
        this.extra = extra;
        this.auth = auth;
    }

    getValidationError(
        value: number,
        min?: number | null,
        max?: number | null,
        multiple?: number | null,
        isFrom?: boolean,
        currencyCode?: string,
        lang?: string,
        country?: string,
    ): ValidationResult {
        let errorCode = COMMON_AMOUNT_ISSUE;
        let errorMessage: string | undefined;
        let isError = false;
        let isShowToViewDetails = false;
        const canMakeDraftTransaction = this.extra?.canMakeDraftTransaction;
        const safeCurrency = currencyCode ?? '';
        const isZeroRemainingProfileLimits = LimitsService.getZeroProfileLimitsErrors(value, canMakeDraftTransaction, this.profile);
        const { hasThreeMonthLimitErrors } = LimitsService.getThreeMonthLimitsError(
            {
                value,
                canMakeDraftTransaction,
                maxAmount: max ?? undefined,
            },
            this.profile,
        );
        const { hasUsaLimitErrors } = LimitsService.getUsaLimitsErrors(
            value,
            max ?? 0,
            this.auth?.countryId,
            safeCurrency,
            Boolean(isFrom),
            this.extra?.commission,
            this.profile,
        );
        const isAdditionalLimitError = LimitsService.isAdditionalLimitError(value, Boolean(isFrom), this.extra?.commission, this.extra?.additionalAmlLimit);

        if (hasThreeMonthLimitErrors) {
            errorCode = BLOCK_THREE_MONTH_LIMIT_ISSUE;
            isError = true;
            isShowToViewDetails = true;
        } else if (hasUsaLimitErrors && !this.isBonus) {
            errorCode = USA_PROFILE_LIMIT_ISSUE;
            isError = true;
            isShowToViewDetails = true;
        } else if (min && value !== 0 && value < min) {
            errorCode = MIN_AMOUNT_ISSUE;
            const localizedMin = getLocalizedRawAmount(lang, country, min) ?? undefined;
            errorMessage = resolveArguments(this.errorMessages.minValidError, [localizedMin, safeCurrency]);
            isError = true;
        } else if (isZeroRemainingProfileLimits || (max && value > max)) {
            if (this.isBonus) {
                if (!this.leftLimitAmount || (max !== null && max !== undefined && value > max)) {
                    errorCode = MAX_AMOUNT_BONUS_ISSUE;
                    const localizedMax = getLocalizedRawAmount(lang, country, max ?? 0) ?? '';
                    errorMessage = resolveArguments(this.errorMessages.youHave, [`${localizedMax} ${safeCurrency}`]);
                } else if (this.leftLimitAmount && value > this.leftLimitAmount) {
                    errorCode = MAX_AMOUNT_BONUS_LIMIT_ISSUE;
                }
            } else {
                errorCode = MAX_AMOUNT_ISSUE;
            }
            isError = true;
            isShowToViewDetails = true;
        } else if (isAdditionalLimitError) {
            errorCode = ADDITIONAL_LIMIT_ISSUE;
            isError = true;
            isShowToViewDetails = true;
        } else if (!canMakeDraftTransaction && typeof this.leftLimitAmount === 'number' && value > this.leftLimitAmount && isFrom) {
            errorCode = PROFILE_LIMIT_ISSUE;
            isError = true;
            isShowToViewDetails = true;
        } else if (multiple && value % multiple !== 0) {
            errorCode = MULTIPLE_AMOUNT_ISSUE;
            isError = true;
            isShowToViewDetails = true;
        }

        return {
            error: {
                code: errorCode,
                body: errorMessage,
            },
            errorMessage,
            isError,
            isShowToViewDetails,
        };
    }
}
